// §26.4 (D-214) — the invitation card, and why it is **not** a §13 notification.
//
// §13 toasts for six seconds and then falls into the Center. That is exactly wrong for an
// invitation, whose entire value is being present when the other person looks up: a card that has
// already gone is an invitation that failed while both people were sitting at their desks.
//
// So a card **persists until it is answered, cancelled by the sender, or the sender's game window
// closes**. The last clause is the one that is easy to leave out and the one that matters most —
// an invitation that outlives the thing it invites you to is a button that fails when pressed, and
// pressing it is the moment somebody chose to say yes.
//
// The store lives here rather than in the Connections app because the card is drawn on §19.4's
// above-every-frame layer, which is the shell's. An app cannot draw there and cannot fake one.

import { createStore, type StoreApi } from 'zustand/vanilla';

export interface InviteCard {
  /** The play session this invites you to (§26.7). Also the cancel key. */
  readonly sessionId: string;
  /** The sender, as a ref. */
  readonly from: string;
  readonly fromName: string;
  /** An Arcade app id — the game being suggested. */
  readonly gameId: string;
  readonly gameName: string;
  /** Arrival order, so the newest is on top. Monotonic, never a clock (§24.4's habit). */
  readonly seq: number;
}

/**
 * §34.13 (M66.3) — a file offer, on the **same** D-214 surface and for the same reason.
 *
 * A §13 toast would be wrong here for exactly the reason it is wrong for a game suggestion: it goes
 * after six seconds, and an offer that expired while both people were at their desks is an offer
 * that failed for no reason. So it is a card, it persists until it is answered, and it lives on the
 * layer an app cannot draw on — which is what stops an app forging one.
 *
 * It is a **second collection** rather than a variant of `InviteCard`, because the two have nothing
 * in common but the surface: an invitation is withdrawn when the sender's window closes and carries
 * a game to join, an offer is withdrawn when it is answered or expires and carries a file to accept.
 * A union would have to be narrowed at every use, and every use is in a different file.
 */
export interface FileOfferCard {
  /** The offer id (§34.13). Also the withdraw key. */
  readonly id: string;
  /** The sender, as a ref. */
  readonly from: string;
  readonly fromName: string;
  readonly name: string;
  readonly size: number;
  /** Arrival order, newest on top. Monotonic, never a clock. */
  readonly seq: number;
}

export interface InvitesState {
  /** Newest first. Every live invitation, however many; the *view* caps what it draws. */
  cards: InviteCard[];
  /** §34.13 — newest first, same rule, same cap. */
  offers: FileOfferCard[];
}

/** §26.4 — "capped at 3 visible with a `+N more` affordance". */
export const MAX_VISIBLE_INVITES = 3;

export interface InvitesService {
  readonly store: StoreApi<InvitesState>;
  /** Add or replace by `sessionId`, so a re-delivered doorbell does not stack duplicates. */
  offer(card: Omit<InviteCard, 'seq'>): void;
  /** The sender cancelled, or their window closed. Silent when there is nothing to withdraw. */
  withdraw(sessionId: string): void;
  /** The user answered. Removes the card; the caller does whatever accepting means. */
  answer(sessionId: string): InviteCard | null;
  /** Signing out drops every card: they belong to the session that received them (§18 F15). */
  clear(): void;
  /** What the shell draws: the newest three, and how many are behind them. */
  visible(): { cards: InviteCard[]; more: number };

  /** §34.13 — add or replace by offer id, so a re-delivered doorbell does not stack duplicates. */
  offerFile(card: Omit<FileOfferCard, 'seq'>): void;
  /** Answered by somebody else, expired, or gone from the server's list. */
  withdrawFile(id: string): void;
  /** The user answered. Removes the card; the caller does whatever accepting means. */
  answerFile(id: string): FileOfferCard | null;
  visibleFiles(): { cards: FileOfferCard[]; more: number };
}

export function createInvitesService(): InvitesService {
  let seq = 0;
  const store = createStore<InvitesState>(() => ({ cards: [], offers: [] }));

  const service: InvitesService = {
    store,

    offer(card) {
      seq += 1;
      const next: InviteCard = { ...card, seq };
      store.setState((state) => ({
        // Replace rather than append when the session is already offered: the doorbell can fire
        // twice for one invitation (a reconnect re-delivers), and two cards for one game is a
        // second button that does the same thing and a "+1 more" that means nothing.
        cards: [next, ...state.cards.filter((existing) => existing.sessionId !== card.sessionId)],
      }));
    },

    withdraw(sessionId) {
      store.setState((state) => ({
        cards: state.cards.filter((card) => card.sessionId !== sessionId),
      }));
    },

    answer(sessionId) {
      const found = store.getState().cards.find((card) => card.sessionId === sessionId) ?? null;
      if (found !== null) service.withdraw(sessionId);
      return found;
    },

    clear() {
      // Both collections: §18 F15 is about the session, and an offer belongs to it exactly as an
      // invitation does. Leaving the offers behind would put a live Accept button on a signed-out
      // desktop, where pressing it 401s.
      store.setState(() => ({ cards: [], offers: [] }));
    },

    visible() {
      const cards = store.getState().cards;
      return {
        cards: cards.slice(0, MAX_VISIBLE_INVITES),
        more: Math.max(0, cards.length - MAX_VISIBLE_INVITES),
      };
    },

    // ── §34.13 the file-offer half ─────────────────────────────────────────────────────────

    offerFile(card) {
      seq += 1;
      const next: FileOfferCard = { ...card, seq };
      store.setState((state) => ({
        // Replace rather than append: the doorbell can fire twice for one offer (a reconnect
        // re-delivers, and the sender's accept rings the recipient's own tabs too), and two cards
        // for one file is a second Accept button that 410s.
        offers: [next, ...state.offers.filter((existing) => existing.id !== card.id)],
      }));
    },

    withdrawFile(id) {
      store.setState((state) => ({ offers: state.offers.filter((card) => card.id !== id) }));
    },

    answerFile(id) {
      const found = store.getState().offers.find((card) => card.id === id) ?? null;
      if (found !== null) service.withdrawFile(id);
      return found;
    },

    visibleFiles() {
      const offers = store.getState().offers;
      return {
        cards: offers.slice(0, MAX_VISIBLE_INVITES),
        more: Math.max(0, offers.length - MAX_VISIBLE_INVITES),
      };
    },
  };
  return service;
}
